// apps/portals/staff/src/app.tsx — the circulation-desk shell the root route
// mounts. Replaces router.tsx's former bare `() => <Outlet />`.
//
// THE ROOT STILL GATES NOTHING — router.tsx's documented choice is intact. This
// component only decides whether to draw CHROME: /login renders bare, everything
// else gets the shell. Access stays per-screen (<ConsoleLayout> on the desk).
import { Show, Suspense } from "solid-js";
import { Outlet, useNavigate, useRouterState } from "@tanstack/solid-router";
import { useCapabilities, useSession } from "@erp/auth-kit/fe";
import { signOutUser } from "@erp/module-auth/fe";
import { BRAND_PINK } from "@erp/ui/brand";
import { AppShell } from "@erp/ui/app-shell";
import { PageSkeleton } from "@erp/ui/page-skeleton";
import { Toaster } from "@erp/ui/toast";
import { staffNav } from "./nav";

/** Screens that render without chrome — the door, and the phone scanner page. */
const CHROMELESS = ["/login", "/scanner", "/accept-invite", "/reset-password"];

export function App() {
  const auth = useSession();
  const caps = useCapabilities();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // NEVER read auth.session() (solid-query's q.data) while /me is pending: that
  // read SUSPENDS, and the shell is the outermost component, so there is no
  // boundary above it — the whole page would blank until /me answers.
  const session = () => (auth.loading() ? null : auth.session());
  // The sidebar waits for BOTH answers — /me and the install's capabilities
  // manifest (ERP-DEPLOYMENT-MODEL §13) — and draws nothing until it has them:
  // a half-known nav (every module before the manifest says which are
  // installed) would flash entries that then vanish. `caps.modules()` is the
  // enabled set, so a desk deployed without `fin` never offers Payments.
  const modules = () => (auth.loading() || caps.loading() ? [] : staffNav(session(), caps.modules()));
  const bare = () => CHROMELESS.some((p) => pathname() === p || pathname().startsWith(`${p}/`));


  const signOut = async () => {
    try {
      await signOutUser();
    } finally {
      await auth.refetch();
      await navigate({ to: "/login" });
    }
  };

  return (
    <>
      <Toaster />
      <Show
        when={!bare()}
        fallback={
          /* A <div>, not a <main> (P-07, 2026-09-21): on a bare route the SCREEN owns the
             landmark — the login and register doors and ScannerApp each draw their own
             <main>, framed to their own width — and a shell <main> around them made two
             landmarks per page. A bare wire that mounts a module component drawing none
             (CardVerify, AcceptInviteScreen, PasswordResetScreen) adds the <main> itself. */
          <div class="min-h-screen">
            <Outlet />
          </div>
        }
      >
        <AppShell
          modules={modules()}
          pathname={pathname()}
          user={session() ? { name: session()?.name ?? session()?.email ?? "Signed in", email: session()?.email } : null}
          onSignOut={signOut}
          onProfile={() => void navigate({ to: "/profile" })}
          appName="SHIPPRA ERP · Circulation desk"
          brandColor={BRAND_PINK}
        >
          <Suspense fallback={<PageSkeleton id="route-skeleton" />}>
            <Outlet />
          </Suspense>
        </AppShell>
      </Show>
    </>
  );
}
